import { useId } from "react";

import { FormControl, FormHelperText, FormLabel, Grid, Stack } from "@mui/material";
import { FormDialog, FormikTextField, isValidDate } from "@shared";

import { FormikDatePicker } from "@/common/components/fields/formik/formik-date-picker/FormikDatePicker";
import { exchangeSectionLabelSx } from "@/pages/exchange/Exchange.styles";
import { ExchangeableAppointmentPicker } from "@/pages/exchange/components/exchangeable-appointment-picker/ExchangeableAppointmentPicker";
import { RequestedPurposesField } from "@/pages/exchange/components/requested-purposes-field/RequestedPurposesField";
import { useTranslation } from "@/translations";

import { useNewExchangeRequest } from "./hooks/useNewExchangeRequest";

interface Props {
  onClose: () => void;
}

export function NewExchangeRequestDialog({ onClose }: Props) {
  const { t } = useTranslation();
  const ownServiceLabelId = useId();
  const ownServiceErrorId = useId();

  const {
    formik,
    appointments,
    isLoading,
    isError,
    datesSelected,
    rangeValid,
    toggleAppointment,
    handleFromChange,
    clearAppointment,
    submitDisabled,
    appointmentError,
  } = useNewExchangeRequest({ onClose });

  const { from } = formik.values;

  return (
    <FormDialog
      title={t("exchange.newRequestDialog.title")}
      onClose={onClose}
      formik={formik}
      submitLabel={t("exchange.newRequestDialog.submit")}
      submitDisabled={submitDisabled}
      fullWidth
    >
      <Stack spacing={3}>
        <FormControl component="fieldset" fullWidth error={appointmentError !== undefined}>
          <FormLabel component="legend" required id={ownServiceLabelId} sx={exchangeSectionLabelSx}>
            {t("exchange.newRequestDialog.ownServiceSection")}
          </FormLabel>
          <Grid container columnSpacing={2}>
            <Grid size={3}>
              <FormikDatePicker
                formik={formik}
                fieldName="from"
                label={t("exchange.newRequestDialog.from")}
                required
                onValueChange={handleFromChange}
              />
            </Grid>
            <Grid size={3}>
              <FormikDatePicker
                formik={formik}
                fieldName="till"
                label={t("exchange.newRequestDialog.till")}
                required
                minDate={isValidDate(from) ? from : undefined}
                onValueChange={clearAppointment}
              />
            </Grid>
          </Grid>
          <ExchangeableAppointmentPicker
            labelledById={ownServiceLabelId}
            datesSelected={datesSelected}
            rangeValid={rangeValid}
            appointments={appointments}
            isLoading={isLoading}
            isError={isError}
            selectedAppointmentId={formik.values.appointmentId}
            onToggle={toggleAppointment}
            hasError={appointmentError !== undefined}
            describedById={ownServiceErrorId}
          />
          {appointmentError !== undefined && <FormHelperText id={ownServiceErrorId}>{appointmentError}</FormHelperText>}
        </FormControl>

        <RequestedPurposesField formik={formik} />

        <FormikTextField
          formik={formik}
          fieldName="note"
          label={t("exchange.requestedServices.note")}
          multiline
          rows={3}
          placeholder={t("exchange.requestedServices.notePlaceholder")}
        />
      </Stack>
    </FormDialog>
  );
}
